<script>
  import { ckmeans } from 'simple-statistics';
  import { scaleThreshold } from 'd3-scale';
  import { intcomma, pluralize } from 'journalize';
  import { getSortByString, getSortByNumber } from '../../utils/sorters';

  // Components
  import Legend from '../Legend.svelte';
  import Tooltip from '../Tooltip.svelte';
  import Toggle from '../Toggle.svelte';
  import { clamp } from '../../utils/clamp';
  import CountiesMap from './CountiesMap.svelte';
  import CountiesTable from './CountiesTable.svelte';

  // The data
  export let data;
  export let metadata;

  const colorSchemes = {
      confirmed_cases: [
        '#efefef',
        '#BEE9F4',
        '#8FD0E1',
        '#60B7D3',
        '#3995B7',
        '#026F90',
        '#00435c',
      ],
      deaths: [
        '#efefef',
        '#dadfe1',
        '#c2cad3',
        '#a3b6c8',
        '#829db0',
        '#5d7786',
        '#46525f',
      ],
    };

  // Set the default values
  let attribute = 'deaths';
  let adjustment = 'per_100k';
  // Figure out what field to use
  const fieldCrosswalk = {
    total: {
      confirmed_cases: 'confirmed_cases',
      deaths: 'deaths',
    },
    per_100k: {
      confirmed_cases: 'confirmed_cases_per_100k',
      deaths: 'deaths_per_100k',
    },
  };
  $: getField = () => {
    return fieldCrosswalk[adjustment][attribute];
  };
  const attributeButtons = [
    { label: 'Cases', value: 'confirmed_cases' },
    { label: 'Deaths', value: 'deaths' },
  ];
  const adjustmentButtons = [
    { label: 'Per 100k', value: 'per_100k' },
    { label: 'Totals', value: 'total' },
  ];
  const handleAttributeClick = (e) => {
    attribute = e.target.getAttribute('data-value');
  };
  const handleAdjustmentClick = (e) => {
    adjustment = e.target.getAttribute('data-value');
  };
  // Map data and functions
  $: mapData = data.map((d) => ({
    fips: d.fips,
    value: d[getField()],
  }));
  $: mapValues = data.map((d) => d[getField()]);
  $: mapPalette = colorSchemes[attribute];
  $: mapClusters = ckmeans(mapValues, mapPalette.length);
  $: mapGroups = mapClusters.map((d) => {
    const value = d[0];
    if (value < 10) return value;
    return Math.floor(value / 10) * 10;
  });
  $: mapColorScale = scaleThreshold(mapGroups, mapPalette);
  $: mapFillFunc = (v) => (v > 0 ? mapColorScale(v) : mapColorScale.range()[0]);
  // Legend config
  const legendTitles = {
    confirmed_cases: 'Confirmed cases',
    deaths: 'Deaths',
  };
  $: legendTitle = legendTitles[attribute];
  // Tooltip config
  let tooltipSlug = 'tooltip__california-counties';
  let tooltipHeader = '';
  let tooltipSubhed = '';
  let tooltipValues = [];
  let showTooltip = false;
  const tooltipLookup = data.reduce((obj, item) => {
    obj[item.fips] = item;
    return obj;
  }, {});
  const tooltipSubhedLookup = {
    total: 'Grand totals',
    per_100k: 'Per 100,000 residents',
  };
  function handleMapActivateCounty(e, push = 20) {
    showTooltip = true;
    const fips = e.detail.fips;
    const data = tooltipLookup[fips];
    tooltipHeader = data.county;
    tooltipSubhed = tooltipSubhedLookup[adjustment];
    if (adjustment == 'total') {
      tooltipValues = [
        `${intcomma(data.confirmed_cases)} confirmed case${pluralize(
          data.confirmed_cases
        )}`,
        `${intcomma(data.deaths)} death${pluralize(data.deaths)}`,
      ];
    } else {
      tooltipValues = [
        `${intcomma(
          data.confirmed_cases_per_100k.toFixed(1)
        )} confirmed case${pluralize(data.confirmed_cases_per_100k)}`,
        `${intcomma(data.deaths_per_100k.toFixed(1))} death${pluralize(
          data.deaths_per_100k
        )}`,
      ];
    }
    const el = document.getElementById(tooltipSlug);
    const { width, height } = el.getBoundingClientRect();
    const innerWidth = window.innerWidth;
    const innerHeight = window.innerHeight;
    const x = event.clientX;
    const y = event.clientY;
    const left = clamp(x - width / 2, push, innerWidth - width - push);
    const top =
      innerHeight > y + push * 2 + height ? y + push : y - height - push;
    el.style.left = `${left}px`;
    el.style.top = `${top}px`;
  }
  function handleMapDeactivateCounty(e) {
    showTooltip = false;
  }
  // Table stuff
  $: tableCaption = 'Cumulative totals as of June 2023';
  $: tableHeaderList = [
    { text: 'County', class: 'text-left sortable', attr: 'county' },
    { text: 'Cases', class: 'text-right sortable', attr: 'confirmed_cases' },
    {
      text: 'Per 100k',
      class: 'text-right sortable',
      attr: 'confirmed_cases_per_100k',
    },
    {
      text: 'Deaths',
      class: 'text-right left-border sortable',
      attr: 'deaths',
    },
    {
      text: 'Per 100k',
      class: 'text-right sortable',
      attr: 'deaths_per_100k',
    },
  ];

  $: tableRows = (d) => ({
    text: metadata[d.fips].url
      ? `<a href="${metadata[d.fips].url}">${d.county} &raquo;</a>`
      : d.county,
    class: 'text-left',
    sort: d.confirmed_cases,
    values: [
      {
        text: intcomma(d.confirmed_cases),
        class: 'text-right',
      },
      {
        text: intcomma(d.confirmed_cases_per_100k.toFixed(1)),
        class: 'text-right lighter',
      },
      {
        text: intcomma(d.deaths),
        class: 'text-right left-border',
      },
      {
        text: intcomma(d.deaths_per_100k.toFixed(1)),
        class: 'text-right lighter',
      },
    ],
  });

  let sortAttr = 'deaths_per_100k';
  let sortAscending = false;

  const getTableRows = (arr, attr, order) => {
    // Sort the data
    const sortFunc =
      attr == 'county'
        ? getSortByString(attr, order)
        : getSortByNumber(attr, order);
    arr.sort(sortFunc);

    // Return the array formatted for our template
    return arr.map(tableRows);
  };

  $: tableRowList = getTableRows(data, sortAttr, sortAscending);

  function onTableSort(e) {
    const thisAttr = e.detail.attr;
    if (thisAttr === sortAttr) {
      sortAscending = !sortAscending;
    } else {
      sortAttr = thisAttr;
      if (thisAttr == 'county') {
        sortAscending = true;
      } else {
        sortAscending = false;
      }
    }
  }
</script>

<div class="svg-container svg-container__california-counties">
  <table class="dg-table">
    <caption>{tableCaption}</caption>
  </table>
  <div class="button-grid california-counties-controls">
    <div class="button-grid-group">
      <Toggle
        label="Metric"
        slug="california-counties-metric"
        buttonList={attributeButtons}
        selectedValue={attribute}
        on:click={handleAttributeClick}
      />
    </div>
    <div class="button-grid-group">
      <Toggle
        label="Method"
        slug="california-counties-adjustment"
        buttonList={adjustmentButtons}
        selectedValue={adjustment}
        on:click={handleAdjustmentClick}
      />
    </div>
  </div>
  <div class="legend">
    <Legend height="60" bind:color={mapColorScale} bind:title={legendTitle} />
  </div>
  <p id="info"><i class="LATSearch01" /> Hover for more information.</p>
  <div id="d3-map-california-counties">
    <CountiesMap
      bind:data={mapData}
      bind:colorScale={mapFillFunc}
      on:mapActivateCounty={handleMapActivateCounty}
      on:mapDeactivateCounty={handleMapDeactivateCounty}
    />
  </div>
  <CountiesTable
    bind:caption={tableCaption}
    bind:headerList={tableHeaderList}
    bind:rowList={tableRowList}
    bind:sortAttr
    bind:sortAscending
    on:sortTable={onTableSort}
  />
  <Tooltip
    slug={tooltipSlug}
    bind:showTooltip
    bind:header={tooltipHeader}
    bind:subhead={tooltipSubhed}
    bind:values={tooltipValues}
  />
</div>
